/**
 * Pure state machine for hands-free voice mode.
 *
 * Voice mode is a loop: listen -> transcribe -> think -> speak -> listen. The
 * audio callbacks that drive it (MediaRecorder, fetch, HTMLAudioElement) all
 * resolve asynchronously and can land *after* the session has already moved on
 * - a `PLAYBACK_FINISHED` arriving after the user barged in is the common one.
 * Keeping the transitions in a pure reducer means those races are decided in
 * one place and can be tested without a browser.
 *
 * The rule throughout: an event that is not valid for the current state is
 * ignored, never applied late.
 */

export type VoiceSessionState =
  | "idle"
  | "listening"
  | "transcribing"
  | "thinking"
  | "speaking";

/**
 * Short, user-facing hint shown in the overlay. Kept as a code rather than a
 * string so the machine stays free of i18n concerns.
 */
export type VoiceNotice =
  | "not_understood"
  | "still_there"
  | "transcription_failed"
  | "answer_failed"
  | "playback_failed"
  | "mic_failed";

export type VoiceSessionEvent =
  | { type: "SESSION_RESET" }
  | { type: "SESSION_STARTED" }
  | { type: "SESSION_ENDED" }
  | { type: "MIC_FAILED" }
  | { type: "SPEECH_CAPTURED" }
  | { type: "NO_SPEECH_DETECTED" }
  | { type: "TRANSCRIBED"; text: string }
  | { type: "TRANSCRIPTION_EMPTY" }
  | { type: "TRANSCRIPTION_FAILED" }
  | { type: "ANSWER_RECEIVED"; text: string }
  | { type: "ANSWER_FAILED" }
  | { type: "PLAYBACK_FINISHED" }
  | { type: "PLAYBACK_FAILED" }
  | { type: "BARGE_IN" }
  | { type: "INTERRUPTED" };

export interface VoiceSessionContext {
  state: VoiceSessionState;
  /** What the user last said, as transcribed. */
  transcript: string;
  /** What Tally last said (or is saying). */
  spokenText: string;
  /** Transient hint for the overlay; cleared as soon as the loop moves on. */
  notice: VoiceNotice | null;
  /**
   * Consecutive rounds where we armed the mic and got nothing usable. Guards
   * against an abandoned session holding the microphone open indefinitely.
   */
  idleRounds: number;
  /**
   * Set when the machine decides the session should stop. The host hook is
   * responsible for acting on it (releasing the mic, closing the overlay).
   */
  shouldEnd: boolean;
}

/** Consecutive silent/unintelligible rounds before the session gives up. */
export const MAX_IDLE_ROUNDS = 3;

export const initialVoiceSessionContext: VoiceSessionContext = {
  state: "idle",
  transcript: "",
  spokenText: "",
  notice: null,
  idleRounds: 0,
  shouldEnd: false,
};

function endSession(context: VoiceSessionContext): VoiceSessionContext {
  return { ...context, state: "idle", notice: null, shouldEnd: true };
}

/**
 * Handles every way a listening round can come back empty: no speech at all, a
 * transcript that is blank, or one that is too short to be a real question.
 * After MAX_IDLE_ROUNDS of these in a row the session ends rather than looping
 * forever on an open microphone.
 */
function unproductiveRound(
  context: VoiceSessionContext,
  notice: VoiceNotice,
): VoiceSessionContext {
  const idleRounds = context.idleRounds + 1;
  if (idleRounds >= MAX_IDLE_ROUNDS) {
    return endSession({ ...context, idleRounds });
  }
  return { ...context, state: "listening", notice, idleRounds };
}

export function voiceSessionReducer(
  context: VoiceSessionContext,
  event: VoiceSessionEvent,
): VoiceSessionContext {
  // Clears `shouldEnd` so the next session's own end is an observable change.
  // Without it, a session that ends and is then restarted into an immediate
  // failure would set `shouldEnd` true when it is already true, and nothing
  // downstream would notice.
  if (event.type === "SESSION_RESET") {
    return initialVoiceSessionContext;
  }

  // Ending and mic failure are accepted from any state - they are not part of
  // the normal loop and must never be dropped as "stale".
  if (event.type === "SESSION_ENDED") {
    return endSession(context);
  }
  if (event.type === "MIC_FAILED") {
    return { ...endSession(context), notice: "mic_failed" };
  }

  switch (context.state) {
    case "idle":
      if (event.type === "SESSION_STARTED") {
        return { ...initialVoiceSessionContext, state: "listening" };
      }
      return context;

    case "listening":
      switch (event.type) {
        case "SPEECH_CAPTURED":
          return { ...context, state: "transcribing", notice: null };
        case "NO_SPEECH_DETECTED":
          return unproductiveRound(context, "still_there");
        default:
          return context;
      }

    case "transcribing":
      switch (event.type) {
        case "TRANSCRIBED":
          // A successful question resets the idle counter.
          return {
            ...context,
            state: "thinking",
            transcript: event.text,
            notice: null,
            idleRounds: 0,
          };
        case "TRANSCRIPTION_EMPTY":
          // Submitting this would ask the assistant an empty question, which in
          // an auto-rearming loop turns into a runaway of junk exchanges.
          return unproductiveRound(context, "not_understood");
        case "TRANSCRIPTION_FAILED":
          return unproductiveRound(context, "transcription_failed");
        default:
          return context;
      }

    case "thinking":
      switch (event.type) {
        case "ANSWER_RECEIVED":
          return {
            ...context,
            state: "speaking",
            spokenText: event.text,
            notice: null,
          };
        case "ANSWER_FAILED":
          return { ...context, state: "listening", notice: "answer_failed" };
        // Talking over the wait is not a way to cancel it (BARGE_IN is ignored
        // here); only an explicit Stop is.
        case "INTERRUPTED":
          return { ...context, state: "listening", notice: null };
        default:
          return context;
      }

    case "speaking":
      switch (event.type) {
        case "PLAYBACK_FINISHED":
          return { ...context, state: "listening", notice: null };
        case "PLAYBACK_FAILED":
          return { ...context, state: "listening", notice: "playback_failed" };
        case "BARGE_IN":
        case "INTERRUPTED":
          return { ...context, state: "listening", notice: null };
        default:
          return context;
      }

    default:
      return context;
  }
}

/** True while the session holds the microphone and should keep the tab awake. */
export function isSessionActive(context: VoiceSessionContext): boolean {
  return context.state !== "idle";
}
